import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {combineLatest, Subscription} from 'rxjs';
import {TranslationService} from '../../../core/services/translation.service';
import {ConfigService} from '../../../core/services/config.service';
import {LoginService} from '../../../core/components/login/login.service';
import {NavigationCommand, NavigationService} from '../../../data/services/navigation.service';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {HupSubscription} from '../../../data/models/subscription.model';
import {BookableCombiOffer} from '../../../data/models/qualified-subscription.model';
import {AdditionalBookingComponent} from '../additional-booking.component';
import {AdditionalBookingOrderedComponent} from '../additional-booking-ordered/additional-booking-ordered.component';

/**
 * Bestätigungsseite der Zubuchung für ein einzelnes Abonnement (P5W-4467, P5W-4460).
 *
 * Route: /additional-booking/:orderId?salesCodeIds=...  - `orderId` ist die BackendId (subId) des
 * Hauptabonnements, `salesCodeIds` sind die Kombi-Werbeschlüssel, die der Kunde auf der Übersicht
 * gewählt hat (eines oder - "Alle buchen" - mehrere).
 *
 * Angezeigt wird nur, was pca für genau diese Auswahl als zubuchbar bestätigt: ein bereits
 * gebuchtes oder nicht (mehr) vorgesehenes Angebot taucht hier nicht auf.
 */
@Component({
  selector: 'app-additional-booking-confirm',
  templateUrl: './additional-booking-confirm.component.html',
  styleUrls: ['./additional-booking-confirm.component.scss']
})
export class AdditionalBookingConfirmComponent implements OnInit, OnDestroy {

  /**
   * Ist die Vorgabe eingeschaltet ('true'/'1'), zeigt die Seite vom Hauptabo nur den Titel,
   * sonst alle Abodaten (Auftragsnummer, Ausgabe, Zeitraum, Lieferadresse).
   */
  public static readonly MAIN_SUBSCRIPTION_TITLE_ONLY_CONFIG_KEY = 'subscription.additionalBooking.showMainSubscriptionTitleOnly';

  protected orderId: string = null;
  /** Das Hauptabonnement, zu dem gebucht wird. */
  protected mainSubscription: HupSubscription = null;
  /** Die von pca bestätigten, zubuchbaren Angebote der Auswahl. */
  protected bookableCombiOffers: BookableCombiOffer[] = [];
  protected errors: string[] = [];
  protected isLoading: boolean = true;
  /** Läuft der Bestätigungs-Request? Solange bleibt der Button gesperrt. */
  protected isBooking: boolean = false;

  private paramSub: Subscription;
  private qualificationSub: Subscription;

  constructor(public translationService: TranslationService,
              private route: ActivatedRoute,
              private router: Router,
              private loginService: LoginService,
              private navigationService: NavigationService,
              private subscriptionService: HupSubscriptionsService,
              private configService: ConfigService,
              @Inject(PLATFORM_ID) private platformId: Object) {
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering liegt kein SSO-Token vor; die Qualifikationsprüfung würde
      // fehlschlagen und einen irreführenden Zustand rendern.
      return;
    }
    this.paramSub = combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(([params, queryParams]) => {
      this.orderId = params.get('orderId');
      this.checkQualification(queryParams.getAll(AdditionalBookingComponent.SALES_CODE_IDS_PARAM));
    });
  }

  ngOnDestroy(): void {
    this.qualificationSub?.unsubscribe();
    this.paramSub?.unsubscribe();
  }

  /** Lässt pca prüfen, welche der gewählten Angebote zu diesem Abo zubuchbar sind. */
  private checkQualification(salesCodeIds: string[]): void {
    this.isLoading = true;
    this.mainSubscription = null;
    this.bookableCombiOffers = [];
    this.errors = [];
    this.qualificationSub?.unsubscribe();

    const requested = salesCodeIds.filter(id => !!id?.trim());
    if (requested.length === 0) {
      console.error(`Zubuchung zu Abonnement ${this.orderId} ohne gewählte Kombi-Werbeschlüssel aufgerufen`);
      this.handleNotQualified();
      return;
    }

    this.qualificationSub = this.subscriptionService.getQualifiedSubscription(this.orderId, requested)
      .subscribe(qualified => {
        if (!qualified.subscription || qualified.bookableCombiOffers.length === 0) {
          this.handleNotQualified();
          return;
        }
        this.mainSubscription = qualified.subscription;
        this.bookableCombiOffers = qualified.bookableCombiOffers;
        this.isLoading = false;
      });
  }

  private handleNotQualified(): void {
    this.errors = [this.translationService.getTranslation('additionalBooking_notQualified')];
    this.isLoading = false;
  }

  /**
   * Nur den Titel des Hauptabos zeigen? Der Key wird vom ConfigResolverService der Route
   * vorgeladen (siehe app-routing.module.ts), daher genügt der synchrone getConfig-Zugriff.
   * Fehlt die Vorgabe, werden alle Abodaten gezeigt.
   */
  public get showMainSubscriptionTitleOnly(): boolean {
    const value = this.configService
      .getConfig(AdditionalBookingConfirmComponent.MAIN_SUBSCRIPTION_TITLE_ONLY_CONFIG_KEY)?.value?.trim().toLowerCase();
    return value === 'true' || value === '1';
  }

  public get isQualified(): boolean {
    return !!this.mainSubscription && this.bookableCombiOffers.length > 0;
  }

  /** "Ihr zubuchbares Produkt" bzw. bei mehreren "Ihre zubuchbaren Produkte". */
  public get productsLabelKey(): string {
    return this.bookableCombiOffers.length > 1
      ? 'additionalBooking_confirm_products_label'
      : 'additionalBooking_confirm_product_label';
  }

  /** Nur mit geprüftem Abo und mindestens einem zubuchbaren Angebot darf gebucht werden. */
  public get canConfirm(): boolean {
    return this.isQualified && !this.isBooking;
  }

  public confirmBooking(): void {
    if (!this.canConfirm) {
      return;
    }
    this.isBooking = true;
    this.errors = [];

    this.loginService.getUserData().subscribe({
      next: userData => this.createCombiSubscriptions(userData.userBackendId),
      error: error => {
        console.error('Kundendaten für die Zubuchung konnten nicht geladen werden', error);
        this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
        this.isBooking = false;
      }
    });
  }

  private createCombiSubscriptions(userBackendId: number): void {
    const salesCodeIds = this.bookableCombiOffers.map(offer => offer.salesCodeId);
    this.subscriptionService.createCombiSubscriptions(userBackendId, Number(this.orderId), salesCodeIds).subscribe({
      next: createdSubscriptions => {
        this.isBooking = false;
        // Kombi-Abos werden direkt in Paradise angelegt; erst eine backendId > 0 belegt, dass
        // wirklich ein Abo entstanden ist. Ohne die wäre die Erfolgsmeldung eine Lüge.
        const created = (createdSubscriptions ?? []).filter(subscription => subscription?.backendId > 0);
        const createdIds = created.map(subscription => String(subscription.backendId));
        if (createdIds.length === 0) {
          console.error(`Zubuchung zu Abonnement ${this.orderId} lieferte kein angelegtes Abonnement`,
            createdSubscriptions);
          this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
          return;
        }
        const notBookedOffers = this.notBookedOffers(created);
        if (createdIds.length < salesCodeIds.length) {
          console.error(`Zubuchung zu Abonnement ${this.orderId}: nur ${createdIds.length} von ${salesCodeIds.length} Kombi-Abos angelegt`,
            notBookedOffers);
        }
        // replaceUrl: nach der Zubuchung ist diese Bestätigungsseite verbraucht - sie könnte für
        // dieselbe Auswahl nur noch "nicht mehr zubuchbar" melden. Der History-Eintrag wird
        // daher ersetzt, damit der Browser-Zurück-Weg von der Bestellbestätigung direkt auf die
        // Übersicht führt statt in diese Sackgasse. Die Auswahl (Query-Parameter) reist nicht mit.
        // Was nicht angelegt wurde, zeigt die Bestellbestätigung mit dem Hinweis auf den Verlag.
        this.router.navigate([NavigationCommand.ADDITIONALBOOKINGORDERED, createdIds.join(',')], {
          replaceUrl: true,
          state: {
            [AdditionalBookingOrderedComponent.NOT_BOOKED_OFFERS_STATE_KEY]: notBookedOffers,
            [AdditionalBookingOrderedComponent.NOT_BOOKED_COUNT_STATE_KEY]: this.notBookedCount(created)
          }
        });
      },
      error: error => {
        console.error(`Zubuchung für Abonnement ${this.orderId} fehlgeschlagen`, error);
        this.errors.push(this.translationService.getTranslation('additionalBooking_error_confirm'));
        this.isBooking = false;
      }
    });
  }

  /**
   * Die Bezeichnungen der gewählten Angebote, die pca NICHT angelegt hat. pca legt je Angebot
   * einzeln an und überspringt ein scheiterndes; welches fehlt, zeigt das Produkt der angelegten
   * Kombi-Abos - je Produkt wird höchstens ein Angebot gebucht.
   *
   * Geht die Zuordnung nicht auf (ein angelegtes Abo ohne Produkt), bleiben die Namen weg - ein
   * angelegtes Angebot als "nicht angelegt" zu nennen, schickte den Kunden grundlos zum Verlag.
   * Dass etwas fehlt, meldet dann noch die Anzahl ({@link notBookedCount}).
   */
  private notBookedOffers(created: HupSubscription[]): string[] {
    if (this.notBookedCount(created) === 0) {
      return [];
    }
    const createdProducts = new Set(created.map(subscription => subscription.productCode).filter(product => !!product));
    const notBooked = this.bookableCombiOffers.filter(offer => !createdProducts.has(offer.productId));
    if (notBooked.length !== this.notBookedCount(created)) {
      console.error(`Zubuchung zu Abonnement ${this.orderId}: die nicht angelegten Angebote lassen sich nicht über das Produkt bestimmen`,
        created);
      return [];
    }
    return notBooked.map(offer => offer.description || offer.salesCodeId);
  }

  /** Wie viele der gewählten Angebote pca nicht angelegt hat. */
  private notBookedCount(created: HupSubscription[]): number {
    return Math.max(this.bookableCombiOffers.length - created.length, 0);
  }

  /** Zurück zur Zubucher-Übersicht, von der aus das Angebot gewählt wurde. */
  public goBack(): void {
    this.navigationService.navigateTo(NavigationCommand.ADDITIONALBOOKINGOVERVIEW);
  }
}
